import { TextInput } from '@mantine/core';
import { useFillOutRevisionDataProvider } from 'components/forms/revision/fill-out/data/FillOutRevisionDataProvider';
import { RevisionFieldProps } from 'components/forms/revision/fill-out/types';
import { IFormInputSpecTextExpression } from 'pages/revisions-module/template-editor/editors/form/types';
import { useEffect, useMemo } from 'react';

/**
 * Evaluates a text expression template at fill-out time.
 *
 * Each `{fieldName}` token in the template is replaced with the current value
 * of the referenced field, read from the correct form context (revision / device).
 */
export default function RevisionFieldTextExpression({
  spec,
  name,
  deviceId,
}: RevisionFieldProps<IFormInputSpecTextExpression>) {
  const { form, findInputByName } = useFillOutRevisionDataProvider();

  const value = useMemo(() => {
    return (spec.template ?? '').replace(/\{([^{}]+)\}/g, (_, fieldName: string) => {
      const input = findInputByName(fieldName);

      if (input?.context === 'revision') {
        return String(form.values.fields[fieldName] ?? '');
      }

      if (input?.context === 'device' && deviceId !== undefined) {
        return String(form.values.devices[String(deviceId)]?.fields?.[fieldName] ?? '');
      }

      return '';
    });
  }, [form.values, spec.template, findInputByName, deviceId]);

  useEffect(() => {
    const current = form.getInputProps(name).value;

    if (current !== value) {
      form.setFieldValue(name, value);
    }
  }, [form, name, value]);

  return (
    <TextInput
      size="lg"
      required={spec.required}
      readOnly
      label={spec.label}
      {...form.getInputProps(name)}
      value={value}
    />
  );
}
